// TODO: Fix eslint issues the next time this file is edited.
/* eslint-disable eqeqeq */
import React, { Fragment, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import {
  ThemeProvider as FelaThemeProvider,
  RendererProvider,
} from 'react-fela';
import Frame, { useFrame } from 'react-frame-component';
import {
  Block,
  Flex,
  Link,
  Spacer,
  styleRenderer,
  StyleTags,
  Text,
  useTheme,
  View,
} from 'vcc-ui';
import lightCodeTheme from '../../utils/code-theme';
import darkCodeTheme from '../../utils/code-theme-dark';
import { MDXComponents } from '../mdx';

const LiveProvider = dynamic(() =>
  import('react-live').then((mod) => mod.LiveProvider)
);
const LiveEditor = dynamic(() =>
  import('react-live').then((mod) => mod.LiveEditor)
);
const LivePreview = dynamic(() =>
  import('react-live').then((mod) => mod.LivePreview)
);
const LiveError = dynamic(() =>
  import('react-live').then((mod) => mod.LiveError)
);

const frameRenderer = styleRenderer({ selectorPrefix: 'frame-' });
frameRenderer.renderStatic('margin:0', 'body');

const LivePreviewWithFrame = () => {
  const { document } = useFrame();
  const theme = useTheme();
  const codeTheme = theme.name == 'volvo-dark' ? darkCodeTheme : lightCodeTheme;
  return (
    <RendererProvider
      renderer={frameRenderer}
      rehydrate={false}
      targetDocument={document}
    >
      <FelaThemeProvider overwrite={false} theme={codeTheme}>
        <LivePreview />
      </FelaThemeProvider>
    </RendererProvider>
  );
};
const CustomLivePreview = ({ viewport }) => {
  const [head, setHead] = useState(null);
  const theme = useTheme();
  const frameRef = useRef(null);
  useEffect(() => {
    if (!viewport || !frameRef.current) return;

    const timeout = setTimeout(() => {
      setHead(<StyleTags renderer={frameRenderer} />);
      if (frameRef.current.contentWindow.document.body) {
        frameRef.current.style.height =
          frameRef.current.contentWindow.document.body.scrollHeight + 'px';
      }
    }, 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [viewport, theme]);
  if (typeof window === 'undefined' && viewport) {
    return null;
  }
  if (!viewport) {
    return <LivePreview />;
  }
  return (
    <Frame
      ref={frameRef}
      head={head}
      frameBorder={0}
      style={{
        width: viewport.includes('px') ? viewport : viewport + 'px',
        border: `1px dotted ${theme.color.foreground.secondary}`,
        borderRadius: 5,
      }}
    >
      <LivePreviewWithFrame />
    </Frame>
  );
};
const MDXCodeBlock = ({
  children,
  live,
  noInline,
  expanded = false,
  viewport,
}) => {
  const code = children.replace(/\n$/g, ''); // Remove newline from end of code
  const [showDetails, setShowDetails] = useState(expanded);

  const theme = useTheme();
  const codeTheme = theme.name == 'volvo-dark' ? darkCodeTheme : lightCodeTheme;

  return (
    <>
      {live ? (
        <LiveProvider
          code={code}
          theme={codeTheme}
          scope={MDXComponents}
          noInline={noInline}
        >
          <Flex extend={{ alignItems: 'flex-start' }}>
            {viewport && (
              <View
                extend={({ theme: { color, baselineSubGrid } }) => ({
                  backgroundColor: color.background.secondary,
                  borderTopRightRadius: 5,
                  borderTopLeftRadius: 5,
                  padding: baselineSubGrid,
                })}
              >
                <Text variant="bates">
                  🔒 Locked viewport @
                  {viewport.includes('px') ? viewport : viewport + 'px'}
                </Text>
              </View>
            )}
            <View
              extend={({ theme: { color, baselineGrid } }) => ({
                background: color.background.primary,
                padding: 3 * baselineGrid,
                borderRadius: 5,
                border: `1px solid ${color.ornament.border}`,
                overflow: 'auto',
                width: '100%',
              })}
            >
              <CustomLivePreview viewport={viewport} />
            </View>
            <View width="100%">
              <Block>
                <Block
                  as="details"
                  open={showDetails}
                  onToggle={(e) => {
                    setShowDetails(e.target.open);
                  }}
                  extend={() => ({
                    transition: 'all 0.2s ease 0s',
                    overflow: 'hidden',
                  })}
                >
                  <Block
                    as="summary"
                    extend={({ theme: { baselineGrid } }) => ({
                      boxSizing: 'border-box',
                      padding: `4px ${baselineGrid}px`,
                      cursor: 'pointer',
                      width: '100%',
                      '::-webkit-details-marker': {
                        display: 'none',
                      },
                    })}
                  >
                    <Link
                      onClick={() =>
                        setShowDetails((showDetails) => !showDetails)
                      }
                      arrow={showDetails ? 'up' : 'down'}
                    >
                      Live editor
                    </Link>
                  </Block>
                  {showDetails && (
                    <>
                      <View
                        extend={({ theme: { color, baselineGrid } }) => ({
                          background: color.background.secondary,
                          padding: 3 * baselineGrid,
                          border: `1px solid ${color.ornament.border}`,
                          borderRadius: 5,
                        })}
                      >
                        <LiveEditor
                          style={{
                            background: theme.color.background.secondary,
                            border: 'none !important',
                            outline: 'none',
                          }}
                        />
                      </View>
                      <LiveError />
                    </>
                  )}
                </Block>
              </Block>
            </View>
            <Spacer size={4} />
          </Flex>
        </LiveProvider>
      ) : (
        <LiveProvider code={code} theme={codeTheme} disabled>
          <Flex>
            <View
              extend={({ theme: { color, baselineGrid } }) => ({
                background: color.background.secondary,
                padding: 2 * baselineGrid,
                border: `1px solid ${color.ornament.border}`,
                borderRadius: 5,
              })}
            >
              <LiveEditor
                style={{
                  background: theme.color.background.secondary,
                  border: 'none !important',
                  outline: 'none',
                }}
              />
            </View>
            <Spacer size={2} />
          </Flex>
        </LiveProvider>
      )}
    </>
  );
};

export default MDXCodeBlock;
